import { queryCache, store } from 'App';
import { environment } from 'config/environment';
import { fold, fromNullable, map } from 'fp-ts/lib/Option';
import { compose, identity } from 'ramda';
import { authActions } from 'state/auth/auth.actions';
import { tokenSelector } from 'state/auth/auth.selectors';
import { summerErrorHandler } from 'core/axios/axiosError.interceptor';
import Axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  CancelToken,
} from 'axios';
import { isNone } from 'fp-ts/es6/Option';
import { setAuthHeader } from '@summer/react-kit';
import { UserSettingsDTO } from 'models/user';
import { extractSettingsLanguage, getLanguageCode } from 'core/intl/intlUtils';
import { languageStorageKey } from 'core/intl/languages';

const apiBaseUrl = environment.apiUrl;
const timeout = 1000 * 60 * 5;

export const BaseAxios = Axios.create({
  baseURL: apiBaseUrl,
  headers: { 'Accept-Language': 'en-GB' },
  timeout,
});

export const BaseAxiosManualError = Axios.create({
  baseURL: apiBaseUrl,
  headers: { 'Accept-Language': 'en-GB' },
  timeout,
});

export const AuthorizedBaseAxios = Axios.create({
  baseURL: apiBaseUrl,
  headers: { 'Accept-Language': 'en-GB' },
  timeout,
});

// ----------- Helpers -----------

export const flatten = <T>(a: AxiosResponse<T>) => a.data;
export const flattenError = <T>(a: AxiosError<T>) => a.response;
export const unsafeFlattenError = (error: unknown) =>
  flattenError(error as AxiosError<unknown>);

export const createCancelToken = (
  msg = 'Operation aborted by the user'
): [CancelToken, () => void] => {
  const source = Axios.CancelToken.source();
  return [source.token, () => source.cancel(msg)];
};

// export const setAuthToConfig = (tokenResolver: () => Option<string>) => (
//   config: AxiosRequestConfig,
// ) =>
//   when(isSome, (token: Option<string>) =>
//     set(lensPath(['headers', 'Authorization']), `Bearer ${(token as Some<string>).value}`, config),
//   )(tokenResolver());

// ----------- BaseAxios interceptors -----------

BaseAxios.interceptors.response.use(identity, summerErrorHandler);

// ----------- AuthorizedBaseAxios interceptors -----------

// compose(
//   (onFulfilled: (config: AxiosRequestConfig) => AxiosRequestConfig) =>
//     AuthorizedBaseAxios.interceptors.request.use(onFulfilled),
//   setAuthToConfig
// )(() => tokenSelector(store.getState()));
AuthorizedBaseAxios.interceptors.request.use((config: AxiosRequestConfig) => {
  const token = tokenSelector(store.getState());

  if (isNone(token)) {
    return config;
  }

  return setAuthHeader(token.value)(config);
});

const appendLanguageHeader = () => {
  const settingsData = queryCache.getQuery<UserSettingsDTO[]>('user-settings')
    ?.state.data;

  const browserLang = getLanguageCode(navigator.language);
  const localStorageLang = localStorage.getItem(languageStorageKey);

  return (
    (settingsData ? extractSettingsLanguage(settingsData) : localStorageLang) ||
    browserLang ||
    'en-GB'
  );
};

// Language interceptors
BaseAxios.interceptors.request.use((config: AxiosRequestConfig) => {
  config.headers['Accept-Language'] = appendLanguageHeader();

  return config;
});

AuthorizedBaseAxios.interceptors.request.use((config: AxiosRequestConfig) => {
  config.headers['Accept-Language'] = appendLanguageHeader();

  return config;
});

// Refresh token interceptor
// AuthorizedBaseAxios.interceptors.request.use((config: AxiosRequestConfig) => {
//   const authState = authSelector(store.getState());
//
//   if (isNone(authState.credentials)) {
//     return config;
//   }
//
//   const credentials = authState.credentials.value;
//
//   if (isTokenValid(credentials.tokenExpirationDate)) {
//     return setAuthHeader(credentials.token)(config);
//   }
//
//   if (!authState.refreshingToken) {
//     store.dispatch(authActions.refreshToken());
//   }
//
//   return waitForNextToken()
//     .then((token) => setAuthHeader(token)(config))
//     .catch(() => config);
// });

AuthorizedBaseAxios.interceptors.response.use(
  identity,
  (axiosError: AxiosError) =>
    compose(
      fold(
        () => Promise.reject(axiosError),
        (isAuthErr) => {
          if (isAuthErr) {
            store.dispatch(authActions.logoutSuccess());
          }
          return Promise.reject(axiosError);
        }
      ),
      map((e: AxiosResponse) => e.status === 401)
    )(fromNullable(axiosError.response))
);

AuthorizedBaseAxios.interceptors.response.use(identity, summerErrorHandler);
